/**
 * Per-vehicle GPS trail storage (localStorage).
 *
 * Stores recent GPS points for each vehicle so the map can draw a live route
 * (Flash Route) and replay past routes (History) — without any backend table.
 * Car ids are globally unique (Supabase row ids), so they're safe keys and the
 * data stays naturally isolated per vehicle/agency.
 *
 * Point shape: { lat, lng, t (epoch ms), s (speed kph) }
 */

const KEY = (carId) => `gpsTrack_${carId}`;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // keep 7 days
const MAX_POINTS = 5000;                    // hard cap per vehicle
const MIN_DIST_M = 8;                        // skip near-duplicate points (denser path)
const MIN_TIME_MS = 15000;                   // ...unless this much time passed

const _read = (carId) => {
  try {
    const raw = localStorage.getItem(KEY(carId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};
const _write = (carId, arr) => {
  try { localStorage.setItem(KEY(carId), JSON.stringify(arr)); } catch (_) {}
};

const distanceM = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Append a point to a vehicle's local trail (deduped, pruned).
 * @returns {boolean} true if the point was actually stored (i.e. it's a real new
 *   point worth persisting to the backend too), false if skipped as noise.
 */
export const appendTrackPoint = (carId, lat, lng, speed = 0, t = Date.now()) => {
  lat = Number(lat); lng = Number(lng);
  // Use full lat/lng precision; reject only truly missing/invalid coords (0,0 too).
  if (!carId || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return false;
  if (!Number.isFinite(t)) t = Date.now();
  try {
    const arr = _read(carId);
    const last = arr[arr.length - 1];
    if (last) {
      // Drop exact duplicate timestamps (same fix polled twice).
      if (t <= last.t && Math.abs(last.lat - lat) < 1e-6 && Math.abs(last.lng - lng) < 1e-6) return false;
      const dist = distanceM(last.lat, last.lng, lat, lng);
      if (dist < MIN_DIST_M && (t - last.t) < MIN_TIME_MS) return false; // noise/duplicate
    }
    arr.push({ lat: +lat.toFixed(6), lng: +lng.toFixed(6), t, s: Math.round(speed) || 0 });
    const cutoff = t - MAX_AGE_MS;
    let out = arr.filter(p => p.t >= cutoff);
    if (out.length > MAX_POINTS) out = out.slice(out.length - MAX_POINTS);
    _write(carId, out);
    return true;
  } catch (_) {
    return false;
  }
};

/** Get a vehicle's trail within an optional [sinceMs, untilMs] window. */
export const getTrack = (carId, sinceMs = 0, untilMs = 0) => {
  if (!carId) return [];
  const arr = _read(carId);
  const filtered = arr.filter(p =>
    (!sinceMs || p.t >= sinceMs) && (!untilMs || p.t <= untilMs)
  );
  return filtered.sort((a, b) => a.t - b.t);
};

/** Clear a vehicle's stored trail. */
export const clearTrack = (carId) => {
  if (!carId) return;
  try { localStorage.removeItem(KEY(carId)); } catch (_) {}
};
